import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { isOwnerRole } from '../../config/menuCodeMap';
import { Select, Button, PageHeader } from '../../components';
import { OrganisationSettingsSkeleton } from '../../skeletons';
import { organizationSettingsService } from '../../services';
import {
  Building2, MapPin, Info, CalendarClock,
  Upload, Trash2, Loader2, QrCode, Printer, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatDateLong } from '../../utils/dateUtils';
import { shiftQrSvg, printShiftQrPoster } from '../../utils/shiftQrPoster';

const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

/** Stored files come back as /api/files/... paths. */
const fileHref = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_ORIGIN}${url}`);

/**
 * Organisation settings — a care-home / domiciliary MANAGER (role 2 / 4) edits
 * their own organisation's legal + contact details and care-home location.
 * Reached from the top-right profile dropdown.
 * Backed by GET/PUT /api/carehome-manager/organization.
 */

const MANAGER_ROLE_IDS = [2, 4];

const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

const SERVICE_TYPES = [
  'Residential care home',
  'Nursing home',
  'Residential dementia care',
  'Domiciliary care',
  'Supported living',
  'Other',
];

// Section header — icon + title + muted right-hand label.
const SectionHeader = ({ icon: Icon, title, hint }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEF2F8] dark:border-gray-800">
    <h3 className="flex items-center gap-2.5 text-[15.5px] font-bold text-[#0B1220] dark:text-gray-100">
      <Icon className="w-[18px] h-[18px] text-[#2563EB]" />
      {title}
    </h3>
    {hint && <span className="text-[12px] text-[#94A3B8] dark:text-gray-500 truncate">{hint}</span>}
  </div>
);

// Labeled input in the app's design language (light + dark).
const Labeled = ({ label, children, full = false }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls =
  'w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] text-[#0B1220] dark:text-gray-200 text-[14px] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none';

const empty = {
  name: '', trading_name: '', email: '', phone: '', address: '',
  care_home_name: '', service_type: '', location_address: '', bed_capacity: '', cqc_location_id: '',
  registered_manager: '', emergency_contact: '',
  time_zone: '', week_starts: '', day_handover_time: '', night_handover_time: '', default_notification_recipients: '',
  leave_unit: 'both', default_leave_days: '28',
};

/**
 * How this organisation measures leave. The choice decides what every one of its
 * people is offered when they apply, and which unit their balance is quoted in —
 * so the descriptions here have to be about consequences, not settings.
 *
 * Mirrors LEAVE_UNITS in api/utils/leaveBalance.js, which enforces it.
 */
const LEAVE_UNITS = [
  {
    value: 'days',
    label: 'Days only',
    hint: 'Staff request whole days. Hourly leave is not offered and is refused if attempted.',
  },
  {
    value: 'hours',
    label: 'Hours',
    hint: 'Balances are shown in hours. Staff can take part of a shift, a block of hours, '
      + 'or pick dates for a longer break — whichever they choose is quoted back in hours.',
  },
  {
    value: 'both',
    label: 'Days and hours',
    hint: 'Everything available. Balances are shown in days, with hours alongside.',
  },
];

/**
 * Shift check-in QR — care homes only. The code carers must scan in the app
 * to start and to end a shift (api/utils/shiftQr.js). One per site, and it
 * acts at once rather than with "Save changes", like the logo.
 */
const ShiftQrCard = ({ multiSite }) => {
  const [qr, setQr] = useState(null);
  const [preview, setPreview] = useState('');
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const show = async (data) => {
    const svg = await shiftQrSvg(data.payload);
    setQr(data);
    setPreview(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await organizationSettingsService.getShiftQrCode();
        if (active && res.success) await show(res.data);
        else if (active) setFailed(true);
      } catch {
        if (active) setFailed(true);
      }
    })();
    return () => { active = false; };
  }, []);

  const print = async () => {
    try {
      setBusy(true);
      await printShiftQrPoster({ ...qr, logo_url: fileHref(qr.logo_url) });
    } catch {
      toast.error('Could not open the print window.');
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm(
      'Regenerate the shift QR code?\n\nThe code already on the wall stops working straight away — '
      + 'nobody can start a shift until the new one is printed and put up.',
    )) return;
    try {
      setBusy(true);
      const res = await organizationSettingsService.regenerateShiftQrCode();
      if (res.success) {
        await show(res.data);
        toast.success(res.message || 'New QR code made.');
      } else {
        toast.error(res.message || 'Could not make a new QR code.');
      }
    } catch (error) {
      toast.error(error.message || 'Could not make a new QR code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={CARD}>
      <SectionHeader icon={QrCode} title="Shift check-in QR" hint={multiSite ? 'Only this site' : 'Care home'} />
      <div className="p-6 flex flex-col sm:flex-row gap-6 items-start">
        <div className="shrink-0 w-[168px] h-[168px] rounded-2xl border border-[#E2E8F0] dark:border-gray-700 bg-white p-3 flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="Shift check-in QR code" className="w-full h-full" />
          ) : failed ? (
            <span className="text-[12px] text-[#94A3B8] text-center">Could not load the code. Refresh the page to try again.</span>
          ) : (
            <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-200 mb-1">
            Carers scan this code to start and end their shift
          </p>
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 leading-relaxed">
            Print it and put it up where staff arrive and leave. In the ServPatch app, Start shift
            and End shift open the camera, and the shift only starts or ends once this code is
            scanned — there is no way to skip it.
            {multiSite && " Each site has its own code, and only this site's code works for its staff."}
          </p>
          {qr && (
            <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
              Code ref <strong className="text-[#334155] dark:text-gray-300">{qr.reference}</strong> · made{' '}
              {formatDateLong(qr.generated_at)}. The same ref is printed on the poster, so you can check
              the one on the wall is the current code.
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-4">
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={!qr || busy}
              leftIcon={<Printer className="w-4 h-4" />}
              onClick={print}
            >
              Print poster
            </Button>
            <Button
              type="button"
              variant="danger-soft"
              size="sm"
              disabled={!qr || busy}
              leftIcon={<RefreshCw className="w-4 h-4" />}
              onClick={regenerate}
            >
              Regenerate
            </Button>
          </div>
          <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
            Regenerate if the code may have been copied or photographed. The old printout stops
            working at once, so put the new one up straight away.
          </p>
        </div>
      </div>
    </div>
  );
};

const OrganisationSettings = () => {
  const { user } = useAuth();
  const roleId = Number(user?.role?.id);
  const allowed = MANAGER_ROLE_IDS.includes(roleId) || isOwnerRole(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);

  // The logo lives outside `form`: it is a file with its own endpoint, saved
  // the moment it is chosen rather than when the form is submitted.
  const [logoUrl, setLogoUrl] = useState('');

  // Multi-site groups (see api orgSettings.controller): the "Organisation" and
  // "Leave policy" cards are the WHOLE GROUP's and only the account owner may
  // change them; the location card and the site name belong to the site open
  // in the top bar. For an ordinary organisation everything is editable as
  // before and nothing here shows.
  const [multiSite, setMultiSite] = useState(false);
  const [canEditGroup, setCanEditGroup] = useState(true);
  const [siteName, setSiteName] = useState('');
  // From the settings response (the site open in the top bar), not the stored
  // user — the QR card only exists for care homes, and the API refuses others.
  const [isCareHome, setIsCareHome] = useState(false);
  const lockGroup = multiSite && !canEditGroup;
  const groupHint = multiSite ? 'Whole group · every site' : null;
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);

  // The section's right-hand label reflects the logged-in user's real role.
  const roleTitle = user?.role?.role_name || user?.job_title || 'Manager';
  // Care home location is a care-home concept — hidden for domiciliary
  // organisations, which have no fixed care-home site.
  const isDomiciliary = String(user?.organization?.organization_type || '').toLowerCase() === 'domiciliary';

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const chooseLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setLogoBusy(true);
      const res = await organizationSettingsService.uploadOrganizationLogo(file);
      if (res.success) {
        setLogoUrl(res.data?.logo_url || '');
        toast.success(res.message || 'Logo updated.');
      } else {
        toast.error(res.message || 'Could not save the logo.');
      }
    } catch (error) {
      toast.error(error.message || 'Could not save the logo.');
    } finally {
      setLogoBusy(false);
    }
  };

  const dropLogo = async () => {
    if (!window.confirm('Remove the logo? Emails will show the first letter of your name instead.')) return;
    try {
      setLogoBusy(true);
      const res = await organizationSettingsService.removeOrganizationLogo();
      if (res.success) {
        setLogoUrl('');
        toast.success(res.message || 'Logo removed.');
      }
    } catch (error) {
      toast.error(error.message || 'Could not remove the logo.');
    } finally {
      setLogoBusy(false);
    }
  };

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    let active = true;
    (async () => {
      try {
        const res = await organizationSettingsService.getOrganizationSettings();
        if (active && res.success) {
          const d = res.data || {};
          setLogoUrl(d.logo_url || '');
          setMultiSite(d.is_multi_site === true);
          setCanEditGroup(d.can_edit_group !== false);
          setSiteName(d.site_name || '');
          setIsCareHome(String(d.organization_type || '').toLowerCase() === 'carehome');
          setForm({
            name: d.name ?? '',
            trading_name: d.trading_name ?? '',
            email: d.email ?? '',
            phone: d.phone ?? '',
            address: d.address ?? '',
            care_home_name: d.care_home_name ?? '',
            service_type: d.service_type ?? '',
            location_address: d.location_address ?? '',
            bed_capacity: d.bed_capacity ?? '',
            cqc_location_id: d.cqc_location_id ?? '',
            registered_manager: d.registered_manager ?? '',
            emergency_contact: d.emergency_contact ?? '',
            time_zone: d.time_zone ?? '',
            week_starts: d.week_starts ?? '',
            day_handover_time: d.day_handover_time ?? '',
            night_handover_time: d.night_handover_time ?? '',
            default_notification_recipients: d.default_notification_recipients ?? '',
            // Both columns are NOT NULL server-side, but an organisation saved
            // before these existed can still come back without them — fall
            // back to the same defaults the database uses rather than to ''.
            leave_unit: d.leave_unit || 'both',
            default_leave_days: d.default_leave_days ?? 28,
          });
        }
      } catch (err) {
        toast.error(err.message || 'Could not load organisation settings');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [allowed]);

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!lockGroup && !form.name.trim()) { toast.error('Legal organisation name is required'); return; }
    if (multiSite && !siteName.trim()) { toast.error('The site needs a name'); return; }
    // Checked here as well as server-side because the column is NOT NULL: a
    // blank or nonsense figure would be rejected by the API and take the whole
    // page's save down with it, which reads as "nothing saved" for a mistake in
    // one field.
    const leaveDays = Number(form.default_leave_days);
    if (!Number.isFinite(leaveDays) || leaveDays < 0 || leaveDays > 366) {
      toast.error('Default annual leave must be a number of days between 0 and 366');
      return;
    }
    setSaving(true);
    try {
      const res = await organizationSettingsService.updateOrganizationSettings({
        ...form,
        bed_capacity: form.bed_capacity === '' ? null : Number(form.bed_capacity),
        default_leave_days: leaveDays,
        ...(multiSite && { site_name: siteName.trim() }),
      });
      if (!res.success) {
        toast.error(res.message || 'Failed to save');
        return;
      }
      toast.success('Organisation settings saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save organisation settings');
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <div className="w-full">
        <PageHeader title="Organisation settings" />
        <div className={`${CARD} p-8 text-center`}>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400">
            You don't have permission to view organisation settings.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <OrganisationSettingsSkeleton />;
  }

  return (
    <div className="w-full pb-10">
      <PageHeader
        title="Organisation settings"
        subtitle="Manage your organisation's details and location."
        actions={
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        }
      />

      <form onSubmit={handleSave} className="flex flex-col gap-5 mt-1">
        {/* Multi-site groups: the site open in the top bar. */}
        {multiSite && (
          <div className={CARD}>
            <SectionHeader icon={MapPin} title="This site" hint="Only this site" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6">
              <Labeled label="Site name">
                <input type="text" value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="e.g. Oak House" className={inputCls} />
              </Labeled>
              <div className="sm:col-span-2 flex items-start gap-2 rounded-xl border border-[#DBE7FF] dark:border-blue-900/40 bg-[#F5F9FF] dark:bg-blue-900/10 px-3.5 py-2.5">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB] dark:text-blue-400" />
                <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                  This is how the site appears in the site list at the top of the page. The location
                  details below are for this site only; the organisation details and leave policy are
                  shared by every site{lockGroup ? ' and are set by the account owner' : ''}.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Organisation */}
        <div className={CARD}>
          <SectionHeader icon={Building2} title="Organisation" hint={groupHint || roleTitle} />
          <fieldset disabled={lockGroup} className={`grid grid-cols-1 sm:grid-cols-2 gap-4 p-6 min-w-0 ${lockGroup ? 'opacity-60' : ''}`}>
            <Labeled label="Legal organisation name">
              <input type="text" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Yellow Care Home Group Ltd" className={inputCls} />
            </Labeled>
            <Labeled label="Trading name">
              <input type="text" value={form.trading_name} onChange={(e) => set({ trading_name: e.target.value })} placeholder="e.g. Yellow Care Home" className={inputCls} />
            </Labeled>
            <Labeled label="Main email">
              <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="admin@example.co.uk" className={inputCls} />
            </Labeled>
            <Labeled label="Main telephone">
              <input type="text" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="020 7946 0000" className={inputCls} />
            </Labeled>
            <Labeled label="Registered address" full>
              <input type="text" value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="Street, City, Postcode" className={inputCls} />
            </Labeled>

            {/* The logo. Saved on choosing rather than with the form, because
                it is a file — and shown at the size it actually appears at in
                an email, so nobody uploads a banner and wonders why it is a
                46px square. */}
            <div className="sm:col-span-2 pt-2 border-t border-[#E2E8F0] dark:border-gray-800">
              <p className="text-[13px] font-semibold text-[#334155] dark:text-gray-300 mt-4 mb-1">Logo</p>
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-3">
                Shown at the top of every email your candidates and staff receive, so the message
                comes from you rather than from the software. Without one they see the first letter
                of your name instead.
              </p>

              <div className="flex flex-wrap items-center gap-4">
                {logoUrl ? (
                  <img
                    src={fileHref(logoUrl)}
                    alt=""
                    className="w-[46px] h-[46px] rounded-[11px] object-cover border border-[#E2E8F0] dark:border-gray-700"
                  />
                ) : (
                  <div
                    className="w-[46px] h-[46px] rounded-[11px] bg-[#0B3F8F] text-white flex items-center justify-center text-[22px] font-bold"
                    aria-hidden="true"
                  >
                    {(form.trading_name || form.name || '?').trim().charAt(0).toUpperCase()}
                  </div>
                )}

                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={chooseLogo}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={logoBusy}
                  leftIcon={logoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoUrl ? 'Replace logo' : 'Upload logo'}
                </Button>
                {logoUrl && (
                  <Button
                    type="button"
                    variant="danger-soft"
                    size="sm"
                    disabled={logoBusy}
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    onClick={dropLogo}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2">
                PNG, JPG, WEBP or SVG, up to 2MB. A square image works best.
              </p>
            </div>
          </fieldset>
        </div>

        {/* Care home location — care-home orgs only */}
        {!isDomiciliary && (
        <div className={CARD}>
          <SectionHeader icon={MapPin} title="Care home location" hint={form.care_home_name || form.trading_name || form.name || roleTitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6">
            <Labeled label="Care home name">
              <input type="text" value={form.care_home_name} onChange={(e) => set({ care_home_name: e.target.value })} placeholder="e.g. Yellow Care Home" className={inputCls} />
            </Labeled>
            <Labeled label="Service type">
              <Select
                className="w-full"
                value={form.service_type}
                onChange={(v) => set({ service_type: v })}
                placeholder="Select service type…"
                options={SERVICE_TYPES.map((t) => ({ value: t, label: t }))}
              />
            </Labeled>
            <Labeled label="Location address" full>
              <input type="text" value={form.location_address} onChange={(e) => set({ location_address: e.target.value })} placeholder="Street, City, Postcode" className={inputCls} />
            </Labeled>
            <Labeled label="Bed capacity">
              <input type="number" min="0" value={form.bed_capacity} onChange={(e) => set({ bed_capacity: e.target.value })} placeholder="e.g. 30" className={inputCls} />
            </Labeled>
            <Labeled label="CQC location ID">
              <input type="text" value={form.cqc_location_id} onChange={(e) => set({ cqc_location_id: e.target.value })} placeholder="e.g. 1-123456789" className={inputCls} />
            </Labeled>
            <Labeled label="Registered manager">
              <input type="text" value={form.registered_manager} onChange={(e) => set({ registered_manager: e.target.value })} placeholder="e.g. William Dumphy" className={inputCls} />
            </Labeled>
            <Labeled label="Emergency contact">
              <input type="text" value={form.emergency_contact} onChange={(e) => set({ emergency_contact: e.target.value })} placeholder="e.g. 020 7946 0281" className={inputCls} />
            </Labeled>
            <div className="sm:col-span-2 flex items-start gap-2 rounded-xl border border-[#DBE7FF] dark:border-blue-900/40 bg-[#F5F9FF] dark:bg-blue-900/10 px-3.5 py-2.5">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB] dark:text-blue-400" />
              <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                Updating these details in ServPatch does not update the organisation's official CQC registration.
              </span>
            </div>
          </div>
        </div>
        )}

        {/* Shift check-in QR — care homes only */}
        {isCareHome && <ShiftQrCard multiSite={multiSite} />}

        {/* Leave policy — the unit every one of this organisation's people sees,
            and the allowance new staff start on. Deliberately here rather than
            on the Leave & Approval screen: it is a standing rule for the whole
            organisation, not a decision about one person's request. */}
        <div className={CARD}>
          <SectionHeader
            icon={CalendarClock}
            title="Leave policy"
            hint={groupHint || LEAVE_UNITS.find((u) => u.value === form.leave_unit)?.label || ''}
          />
          <fieldset disabled={lockGroup} className={`p-6 space-y-5 min-w-0 ${lockGroup ? 'opacity-60' : ''}`}>
            <div>
              <p className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-200 mb-1">
                How is leave measured?
              </p>
              <p className="text-[12px] text-[#64748B] dark:text-gray-400 mb-3">
                This decides what your staff are offered when they apply for leave, and the unit
                their remaining allowance is shown in.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                {LEAVE_UNITS.map((u) => {
                  const active = form.leave_unit === u.value;
                  return (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => set({ leave_unit: u.value })}
                      className={`text-left rounded-xl border p-3.5 transition ${active
                        ? 'border-blue-500 bg-[#F5F9FF] dark:bg-blue-900/10 ring-2 ring-blue-500/10'
                        : 'border-[#E2E8F0] dark:border-gray-700 hover:border-[#CBD5E1] dark:hover:border-gray-600'
                        }`}
                    >
                      <span className={`block text-[13.5px] font-bold ${active
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-[#0B1220] dark:text-gray-200'}`}
                      >
                        {u.label}
                      </span>
                      <span className="block text-[11.5px] text-[#64748B] dark:text-gray-400 mt-1 leading-relaxed">
                        {u.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Labeled label="Default annual leave (days)">
                <input
                  type="number"
                  min="0"
                  max="366"
                  step="0.5"
                  value={form.default_leave_days}
                  onChange={(e) => set({ default_leave_days: e.target.value })}
                  className={inputCls}
                />
              </Labeled>
            </div>

            <div className="flex items-start gap-2 rounded-xl border border-[#DBE7FF] dark:border-blue-900/40 bg-[#F5F9FF] dark:bg-blue-900/10 px-3.5 py-2.5">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB] dark:text-blue-400" />
              <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                {/* The two things about this that surprise people, said before
                    they surprise them. */}
                Changing the default does <strong className="text-[#0B1220] dark:text-gray-200">not</strong> alter
                allowances already in use — nobody's booked year shifts under them. It applies to
                allowances set from now on. Raise an individual's allowance under
                <strong className="text-[#0B1220] dark:text-gray-200"> Leave &amp; Approval → Allowances</strong>.
                <br />
                A day of leave is measured as one of that person's own shifts, so 8 hours off a
                4-hour shift costs two days — and a week off costs a three-day-a-week carer three
                days, not seven.
              </span>
            </div>
          </fieldset>
        </div>

      </form>
    </div>
  );
};

export default OrganisationSettings;
